'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Hook to track page views automatically on route changes
 */
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Analytics] Page view:', pathname);
      return;
    }

    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) {
      console.warn('[Analytics] GA_MEASUREMENT_ID not configured');
      return;
    }

    // Initialize gtag if not already loaded
    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function() {
        window.dataLayer?.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_path: pathname,
      });
    }

    // Track page view
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag?.('event', 'page_view', {
      page_path: url,
      page_title: document.title,
    });
  }, [pathname, searchParams]);
}

/**
 * Track custom events
 */
export function trackEvent(
  eventName: string,
  eventParams: Record<string, any> = {}
) {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Analytics] Event:', eventName, eventParams);
    return;
  }

  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

/**
 * Track RSVP conversion funnel
 */
export function trackRSVPStep(step: string, data?: Record<string, any>) {
  trackEvent('rsvp_step', {
    step_name: step,
    ...data,
  });
}

export function trackRSVPConversion(data: {
  guestCount: number;
  attendingCeremony: boolean;
  attendingReception: boolean;
  mealPreference: string;
}) {
  trackEvent('rsvp_completed', {
    value: data.guestCount,
    guest_count: data.guestCount,
    attending_ceremony: data.attendingCeremony,
    attending_reception: data.attendingReception,
    meal_preference: data.mealPreference,
  });
}

/**
 * Track gallery interactions
 */
export function trackGalleryView(imageId: string, category: string) {
  trackEvent('gallery_image_view', {
    image_id: imageId,
    category: category,
  });
}

export function trackPhotoUpload(data: {
  category: string;
  fileCount: number;
  totalSize: number;
}) {
  trackEvent('photo_upload', {
    category: data.category,
    file_count: data.fileCount,
    total_size_mb: (data.totalSize / 1048576).toFixed(2),
  });
}

export function trackPhotoUploadStart() {
  trackEvent('photo_upload_start');
}

export function trackPhotoUploadSuccess(fileCount: number) {
  trackEvent('photo_upload_success', {
    file_count: fileCount,
  });
}

export function trackPhotoUploadError(error: string) {
  trackEvent('photo_upload_error', {
    error_message: error,
  });
}

/**
 * Track user engagement
 */
export function trackButtonClick(buttonName: string, location: string) {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
}

export function trackFormStart(formName: string) {
  trackEvent('form_start', {
    form_name: formName,
  });
}

export function trackFormSubmit(formName: string, success: boolean) {
  trackEvent('form_submit', {
    form_name: formName,
    success: success,
  });
}

/**
 * Track navigation patterns
 */
export function trackNavigation(from: string, to: string) {
  trackEvent('navigation', {
    from_page: from,
    to_page: to,
  });
}

/**
 * Track time spent on page
 */
let pageStartTime: number | null = null;

export function startPageTimer() {
  pageStartTime = Date.now();
}

export function trackPageDuration(pageName: string) {
  if (pageStartTime) {
    const duration = Math.round((Date.now() - pageStartTime) / 1000);
    trackEvent('page_duration', {
      page_name: pageName,
      duration_seconds: duration,
    });
    pageStartTime = null;
  }
}